// src/pages/LandingPage.tsx

import { useRef, useState, useEffect, memo, useMemo } from "react";
import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Heart, ChevronRight, Music, Loader2 } from "lucide-react";
import { tiktokPixelService } from "../services/tiktok-pixel.service";
import landingBg from "../assets/img/landing3.png";
import { useCampaign } from "../campaigns/providers/CampaignProvider";
import { CampaignModal } from "../campaigns/components/CampaignModal";

const DEFAULT_OCCASIONS = [
  "For yourself.",
  "For family and friends.",
  "For love and appreciation.",
  "For healing and reconnection.",
  "For celebration.",
] as const;

const ANIMATION_CONFIG = {
  textRotate: {
    interval: 2500,
    duration: 0.6,
  },
} as const;

const useMouseParallax = (isMobile: boolean) => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const smoothX = useSpring(mouseX, { damping: 20, stiffness: 150 });
  const smoothY = useSpring(mouseY, { damping: 20, stiffness: 150 });

  const transforms = {
    gold: {
      x: useTransform(smoothX, [-0.5, 0.5], ["-1.5%", "1.5%"]),
      y: useTransform(smoothY, [-0.5, 0.5], ["-1.0%", "1.0%"]),
    },
    rose: {
      x: useTransform(smoothX, [-0.5, 0.5], ["1.0%", "-1.0%"]),
      y: useTransform(smoothY, [-0.5, 0.5], ["1.5%", "-1.5%"]),
    },
  };

  useEffect(() => {
    if (isMobile) return;

    const handleMouseMove = (e: MouseEvent) => {
      mouseX.set(e.clientX / window.innerWidth - 0.5);
      mouseY.set(e.clientY / window.innerHeight - 0.5);
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [isMobile, mouseX, mouseY]);

  return transforms;
};

const RotatingText = memo(({ index, items }: { index: number; items: readonly string[] }) => (
  <div className="h-6 overflow-hidden relative">
    <AnimatePresence mode="wait">
      <motion.div
        key={`${index}-${items[index]}`}
        initial={{ y: 12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -12, opacity: 0 }}
        transition={{ duration: ANIMATION_CONFIG.textRotate.duration }}
      >
        <p className="text-[9px] md:text-[10px] font-semibold uppercase tracking-[0.38em] text-amber-300/75">
          {items[index]}
        </p>
      </motion.div>
    </AnimatePresence>
  </div>
));

const CTAButton = memo(({ onClick, label }: { onClick: () => void; label: string }) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (loading) return;

    setLoading(true);

    try {
      await Promise.resolve(onClick());
    } finally {
      setTimeout(() => setLoading(false), 1200);
    }
  };

  return (
    <motion.button
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={!loading ? { scale: 1.04 } : {}}
      whileTap={!loading ? { scale: 0.96 } : {}}
      disabled={loading}
      onClick={handleClick}
      className={`group flex items-center gap-4 px-10 py-4 rounded-full
        font-bold uppercase tracking-[0.14em] text-[11px]
        shadow-[0_20px_40px_rgba(0,0,0,0.4)]
        transition-all
        ${
          loading
            ? "bg-neutral-200 text-neutral-600 cursor-not-allowed"
            : "bg-white text-black hover:bg-white/95"
        }`}
    >
      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-3"
          >
            <Loader2 size={16} className="animate-spin" />
            <span>Preparing...</span>
          </motion.div>
        ) : (
          <motion.div
            key="idle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-4"
          >
            <Heart
              size={16}
              className="fill-black text-black group-hover:scale-110 transition-transform"
            />
            <span>{label}</span>
            <ChevronRight size={16} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
});

const Footer = memo(({ onNavigate }: { onNavigate: (path: string) => void }) => (
  <footer className="absolute bottom-8 w-full px-6 md:px-12 flex flex-col md:flex-row gap-4 md:gap-0 justify-between items-center md:items-end z-30">
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-4 opacity-40">
        <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
        <span className="text-[10px] font-black uppercase tracking-[0.4em]">LRNSY</span>
      </div>
    </div>

    <div className="flex flex-wrap items-center justify-center gap-3 text-[10px] uppercase tracking-[0.22em] text-white/30">
      <button
        type="button"
        onClick={() => onNavigate("/privacy")}
        className="hover:text-white transition-colors"
      >
        Privacy
      </button>

      <span className="h-1 w-1 rounded-full bg-white/20" />

      <button
        type="button"
        onClick={() => onNavigate("/terms")}
        className="hover:text-white transition-colors"
      >
        Terms
      </button>

      <span className="h-1 w-1 rounded-full bg-white/20" />

      <button
        type="button"
        onClick={() => onNavigate("/settings/privacy")}
        className="hover:text-white transition-colors"
      >
        Data choices
      </button>
    </div>

    <div className="hidden md:flex flex-col items-end gap-1 opacity-20">
      <span className="text-[9px] font-bold uppercase tracking-[0.4em]">
        Intentional Expression
      </span>
      <div className="h-px w-12 bg-white/40 mt-1" />
    </div>
  </footer>
));

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const { activeCampaign } = useCampaign();

  const transforms = useMouseParallax(isMobile);

  const occasions = useMemo(
    () => activeCampaign?.copy.landingOccasions || DEFAULT_OCCASIONS,
    [activeCampaign]
  );

  const heroEyebrow = activeCampaign?.copy.heroEyebrow || "For love, healing & reconnection";
  const heroTitle = activeCampaign?.copy.heroTitle || "Express what matters,";
  const heroSubtitle =
    activeCampaign?.copy.heroSubtitle ||
    "Create private emotional moments for love, appreciation, forgiveness, celebration, healing, and the people who deserve to feel seen.";

  const createLabel = activeCampaign?.copy.createCTA || "Make Someone Feel Seen";

  const handleCreate = () => {
    if (activeCampaign) {
      navigate(`/create?archetype=${activeCampaign.archetype}&campaign=${activeCampaign.slug}`);
      return;
    }

    navigate("/archetypes");
  };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);

    checkMobile();

    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    setIndex(0);

    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % occasions.length);
    }, ANIMATION_CONFIG.textRotate.interval);

    return () => clearInterval(timer);
  }, [occasions]);

  useEffect(() => {
    tiktokPixelService.trackViewContent(
      {
        content_id: activeCampaign?.id || "landing_page",
        content_type: "product",
        content_name: activeCampaign ? `${activeCampaign.name} Landing Page` : "LRNSY Landing Page",
      },
      0,
      "KES",
      activeCampaign ? `viewcontent:campaign:${activeCampaign.id}` : "viewcontent:landing_page"
    );
  }, [activeCampaign]);

  return (
    <div
      ref={containerRef}
      className="relative min-h-screen bg-[#040508] text-white overflow-hidden selection:bg-amber-400 selection:text-black"
    >
      <CampaignModal campaign={activeCampaign} />

      <div className="absolute inset-0 z-0">
        <motion.img
          style={{
            x: !isMobile ? transforms.rose.x : undefined,
            y: !isMobile ? transforms.gold.y : undefined,
          }}
          src={landingBg}
          alt="LRNSY Background"
          className="w-full h-full object-cover scale-[1.03]"
        />

        <div className="absolute inset-0 bg-black/40" />

        <div
          className="absolute inset-0"
          style={{
            background: activeCampaign
              ? `radial-gradient(circle at center, ${activeCampaign.theme.glow} 0%, rgba(0,0,0,0.28) 36%, rgba(0,0,0,0.58) 100%)`
              : `radial-gradient(circle at center, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.0) 34%, rgba(0,0,0,0) 100%)`,
          }}
        />
      </div>

      <main className="relative z-20 flex min-h-screen items-center justify-center px-5 pt-24 pb-20">
        <motion.section
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: isMobile ? 0.6 : 1 }}
          className="
            w-full max-w-2xl mx-auto text-center rounded-[30px]
            border border-white/[0.08] bg-black/[0.18]
            px-6 py-5 sm:px-6 sm:py-5 md:px-7 md:py-6
            shadow-[0_34px_90px_rgba(0,0,0,0.3)]
            backdrop-blur-[4px]
          "
        >
          <p
            className="mb-4 text-[9px] font-semibold uppercase tracking-[0.38em] text-amber-300/70"
            style={{ color: activeCampaign?.theme.primary }}
          >
            {heroEyebrow}
          </p>

          <h1
            className="
              mx-auto max-w-2xl text-[36px] sm:text-[46px]
              md:text-[58px] lg:text-[66px] font-medium
              leading-[1.03] tracking-[-0.045em] text-white/94
            "
          >
            {heroTitle}
            <br />
            <span className="font-serif italic font-normal text-white/82">
              {activeCampaign ? "with intention." : "more intentionally."}
            </span>
          </h1>

          <p className="mt-5 mx-auto max-w-xl text-[14px] sm:text-[15px] md:text-[16px] leading-[1.85] font-normal text-white/62">
            {heroSubtitle}
          </p>

          <div className="mt-6">
            <RotatingText index={index} items={occasions} />
          </div>

          <div className="mt-8 flex flex-col items-center justify-center">
            <CTAButton label={createLabel} onClick={handleCreate} />

            <motion.button
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.08 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/examples")}
              className="
                mt-4 font-serif italic text-sm text-white/45 underline
                underline-offset-4 decoration-white/20 hover:text-white/75
                hover:decoration-white/45 transition-all
              "
            >
              See how others expressed theirs
            </motion.button>
          </div>

          <div className="mt-7 flex items-center justify-center gap-2 opacity-25">
            <Music size={10} />
            <p className="text-[8px] uppercase tracking-[0.35em] font-light">Sound recommended</p>
          </div>
        </motion.section>
      </main>

      <Footer onNavigate={navigate} />
    </div>
  );
};

LandingPage.displayName = "LandingPage";

export default memo(LandingPage);
